// System Action Editor Modal (WP-07).
// Edit configuration for one of the 9 system action buttons (text, template, or flow).
import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  systemActionSave,
  type SystemActionDoc,
  type SystemActionKind,
} from '../../lib/bots';
import { useTemplates } from '../../lib/whatsappApi';
import Toggle from '../ui/Toggle';
import { Button } from '../ui/Button';
import { CloseIcon } from '../icons';
import { useToast } from '../ui/Toast';

interface SystemActionEditorModalProps {
  open: boolean;
  action: SystemActionDoc | null;
  integrationId: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function SystemActionEditorModal({
  open,
  action,
  integrationId,
  onClose,
  onSaved,
}: SystemActionEditorModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const { items: templates } = useTemplates();

  const [enabled, setEnabled] = useState(true);
  const [kind, setKind] = useState<SystemActionKind>('text');
  const [text, setText] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('en');
  const [flowId, setFlowId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (action) {
      setEnabled(action.enabled ?? true);
      setKind(action.kind || 'text');
      setText(action.text || '');
      setTemplateName(action.templateName || '');
      setTemplateLanguage(action.templateLanguage || 'en');
      setFlowId(action.flowId || '');
      setError(null);
    }
  }, [action]);

  if (!open || !action) return null;

  const isStoreAction = action.key === 'confirm_order' || action.key === 'cancel_order';

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await systemActionSave({
        integrationId,
        action: {
          key: action.key,
          enabled,
          kind,
          text: kind === 'text' ? text.trim() : null,
          templateName: kind === 'template' ? templateName.trim() : null,
          templateLanguage: kind === 'template' ? templateLanguage.trim() : null,
          flowId: kind === 'flow' ? flowId.trim() : null,
        },
      });

      if (res.data?.ok) {
        toast.success(t('bots.actions.saved') || 'System action updated successfully');
        onSaved();
        onClose();
      }
    } catch (err) {
      console.error('[SystemActionEditorModal] save error', err);
      const msg = (err as Error).message || 'Failed to save action';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const actionTitle = t(`bots.actions.${action.key}.title`) || action.key;
  const actionDesc = t(`bots.actions.${action.key}.desc`) || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="system-action-editor-title"
        className="relative w-full max-w-lg rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div>
            <h3 id="system-action-editor-title" className="text-base font-bold text-ink dark:text-white">
              {actionTitle}
            </h3>
            {actionDesc && (
              <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">{actionDesc}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          {error && (
            <div className="rounded-lg bg-pink-50 p-3 text-danger dark:bg-pink-950/40 dark:text-pink-300">
              {error}
            </div>
          )}

          {isStoreAction && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
              <span className="font-semibold">{t('bots.actions.storeNoticeTitle') || 'Store Automation Action'}</span>
              <p className="mt-0.5 text-[11px] opacity-90">
                {t('bots.actions.storeNoticeDesc') ||
                  'Used by store automation (WP-12) for cash-on-delivery (COD) order verification and cancellation workflows.'}
              </p>
            </div>
          )}

          {/* Enabled Toggle */}
          <div className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30">
            <div>
              <span className="font-semibold text-ink dark:text-slate-200">
                {t('bots.actions.status') || 'Action Status'}
              </span>
              <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                {t('bots.actions.statusDesc') || 'Enable or disable this automated response.'}
              </p>
            </div>
            <Toggle
              on={enabled}
              onChange={setEnabled}
              label={actionTitle}
            />
          </div>

          {/* Response Kind Selector */}
          <div>
            <label className="block font-semibold text-ink dark:text-slate-200 mb-1.5">
              {t('bots.actions.responseType') || 'Response Type'}
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(['text', 'template', 'flow'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={cn(
                    'rounded-xl border px-3 py-2.5 font-medium transition-all text-center',
                    kind === k
                      ? 'border-brand bg-brand/5 text-brand dark:border-brand dark:bg-brand/10'
                      : 'border-line bg-white text-ink-2 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  )}
                >
                  <span className="capitalize">{t(`bots.actions.kind.${k}`) || k}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Configuration by Kind */}
          {kind === 'text' && (
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('bots.actions.textMessage') || 'Reply Message Text'}
              </label>
              <textarea
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('bots.actions.textPlaceholder') || 'Enter automated response message...'}
                className="w-full rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
              <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-400">
                {t('bots.actions.variableHint') || 'Supports custom variables like {{var:key}} and {{name}}.'}
              </p>
            </div>
          )}

          {kind === 'template' && (
            <div className="space-y-3">
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                  {t('bots.actions.templateName') || 'Approved Message Template'}
                </label>
                {templates && templates.length > 0 ? (
                  <select
                    value={templateName}
                    onChange={(e) => {
                      setTemplateName(e.target.value);
                      const match = templates.find((tpl) => tpl.name === e.target.value);
                      if (match?.language) {
                        setTemplateLanguage(match.language);
                      }
                    }}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <option value="">{t('bots.actions.selectTemplate') || '-- Select Template --'}</option>
                    {templates
                      .filter((tpl) => tpl.status === 'APPROVED')
                      .map((tpl) => (
                        <option key={tpl.id} value={tpl.name}>
                          {tpl.name} ({tpl.language})
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={t('bots.action.keyPlaceholder') || 'e.g. welcome_message'}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                )}
              </div>

              <div>
                <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                  {t('bots.actions.templateLanguage') || 'Template Language Code'}
                </label>
                <input
                  type="text"
                  value={templateLanguage}
                  onChange={(e) => setTemplateLanguage(e.target.value)}
                  placeholder="en, ar, etc."
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            </div>
          )}

          {kind === 'flow' && (
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('bots.actions.flowId') || 'Flow / User Input Flow Identifier'}
              </label>
              <input
                type="text"
                value={flowId}
                onChange={(e) => setFlowId(e.target.value)}
                placeholder="flow_id or input_flow_id"
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              />
              <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-400">
                {t('bots.actions.flowHint') || 'Trigger an interactive visual flow or structured input sequence.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-3 dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button size="sm" onClick={handleSave} loading={saving}>
            {t('common.save') || 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
